/**
 * A keyword given more than once in a SQL Server connection string, read as that format's driver reads it — one rule per
 * format, applied to EVERY keyword (a synonym is the same keyword) before anything is taken from the pairs: the
 * credentials (placeholders, secrets), the fields, Encrypt and Trust server certificate.
 *
 * - SqlClient (ADO.NET): the LAST occurrence. "Connection String Builders": the connection string "is parsed by using a
 *   'last one wins' algorithm"; the synonyms are the ones "SqlConnection.ConnectionString" lists (`Server` / `Address` /
 *   `Addr` / `Network Address` for `Data Source`, `Database` for `Initial Catalog` …).
 * - ODBC: the FIRST occurrence. "SQLDriverConnect Function", Comments: "If any keywords are repeated in the connection
 *   string, the driver uses the value associated with the first occurrence of the keyword." `Addr` is a synonym for
 *   `Address`, and a non-blank `Address` overrides `Server` wherever it stands (the keyword table the ODBC driver's page
 *   links to: "The Address keyword overrides the Server keyword"; "`Address=;` will connect to the server specified in the
 *   Server keyword").
 * - mssql-jdbc: the LAST occurrence. "Setting the connection properties", Remarks: duplicate property names resolve to the
 *   "Last instance in the connection string", and synonyms (`database` / `databaseName`, `userName` / `user`,
 *   `server` / `serverName`, `port` / `portNumber`; from 13.6 the aliases `uid` for `user` and `trusted_connection` for
 *   `integratedSecurity`) "in order, just as it does with duplicate property names".
 *
 * The import sources read with a copy (`electron/connection-import/shared/connectionStringPairs.ts`), pinned to this one
 * by `src/test/coupling/repeatedKeywordsCoupling.test.ts`. Pure; never log what it returns — values carry passwords.
 */

/** Which occurrence of a repeated keyword a driver uses. */
export type DuplicateKeyRule = 'first' | 'last';

/** The SQL Server keyword formats: SqlClient (ADO.NET), ODBC, and mssql-jdbc's `;`-properties. */
export type MssqlKeywordFormat = 'sqlclient' | 'odbc' | 'jdbc';

export type KeywordRules = {
  rule: DuplicateKeyRule;
  /** A synonym (lower-case) → the keyword it names; a key not listed is its own keyword. Only mapped keywords are listed. */
  synonyms: Readonly<Record<string, string>>;
  /** `[winner, loser]`: a non-blank winner removes the loser wherever either stands; a blank one gives way to it. */
  overrides?: ReadonlyArray<readonly [string, string]>;
};

export const MSSQL_KEYWORD_RULES: Readonly<Record<MssqlKeywordFormat, KeywordRules>> = {
  sqlclient: {
    rule: 'last',
    synonyms: {
      server: 'data source', address: 'data source', addr: 'data source', 'network address': 'data source',
      database: 'initial catalog', uid: 'user id', user: 'user id', pwd: 'password', trusted_connection: 'integrated security',
      trustservercertificate: 'trust server certificate',
    },
  },
  odbc: { rule: 'first', synonyms: { addr: 'address' }, overrides: [['address', 'server']] },
  jdbc: {
    rule: 'last',
    synonyms: { database: 'databasename', username: 'user', server: 'servername', port: 'portnumber', uid: 'user', trusted_connection: 'integratedsecurity' },
  },
};

/** The keyword `key` names in `format` (case-insensitive, synonyms folded). */
export function keywordOf(format: MssqlKeywordFormat, key: string): string {
  const name = key.trim().toLowerCase();
  return MSSQL_KEYWORD_RULES[format].synonyms[name] ?? name;
}

/**
 * The pairs the format's driver uses, in the order they appear: one per keyword — its first or last occurrence by the
 * format's rule — less any a non-blank overriding keyword displaces.
 */
export function effectivePairs<T extends { key: string; value: unknown }>(pairs: readonly T[], format: MssqlKeywordFormat): T[] {
  const { rule, overrides = [] } = MSSQL_KEYWORD_RULES[format];
  const chosen = new Map<string, T>();
  for (const pair of pairs) {
    const keyword = keywordOf(format, pair.key);
    if (rule === 'last' || !chosen.has(keyword)) chosen.set(keyword, pair);
  }
  for (const [winner, loser] of overrides) {
    const won = chosen.get(winner);
    if (won && chosen.has(loser)) chosen.delete(String(won.value ?? '').trim() ? loser : winner);
  }
  const used = new Set(chosen.values());
  return pairs.filter((pair) => used.has(pair));
}
